<script setup lang="ts">
import Swiper from 'swiper'
import { Pagination } from 'swiper/modules'
import { onMounted, onUnmounted, ref, type PropType, computed } from 'vue'
import { useBreakpoints } from '@/lib/utils'
import { type BlogThumbnailData } from '@/assets/ts/pages/index'
import {
	createGAEvent,
	GaGeneralEvent,
	GaGeneralPageName,
} from '@/assets/ts/customEvent'

const { isTabletPortrait } = useBreakpoints()

const props = defineProps({
	data: {
		type: Array as PropType<BlogThumbnailData[]>,
		default: () => [],
	},
})

const swiperEl = ref<HTMLElement | null>(null)
let swiper: Swiper | null = null

const swiperOptions = {
	modules: [Pagination],
	pagination: {
		el: '.swiper-pagination',
		type: 'bullets' as const,
		clickable: true,
	},
	slidesPerView: 'auto' as const,
	spaceBetween: 24,
	speed: 500,
	centeredSlides: true,
	centeredSlidesBounds: true,
	allowTouchMove: true,
	breakpoints: {
		1024: {
			spaceBetween: 20,
			allowTouchMove: false,
		},
		1280: {
			spaceBetween: 24,
			allowTouchMove: false,
		},
	},
}

const initializeSwiper = () => {
	if (!swiperEl.value) return
	swiper = new Swiper(swiperEl.value, swiperOptions)
}

const loadedImages = ref<Set<string>>(new Set())
const handleImageLoad = (imgUrl: string) => {
	loadedImages.value.add(imgUrl)
}

const handleBannerClick = (index: number) => {
	createGAEvent(GaGeneralEvent.ButtonClick, {
		page_name: GaGeneralPageName.HomePage,
		button_name: `MKT Banner ${index + 1} Click`,
	})
}

const isLoading = computed(() => !props.data.length)
const placeholders = computed(() => Array(3).fill(null))

const isImageLoaded = (imgUrl: string) => loadedImages.value.has(imgUrl)

onMounted(() => {
	initializeSwiper()
})

onUnmounted(() => {
	swiper?.destroy()
})
</script>

<template>
	<div class="bs-container py-24px mdp:py-32px lg:py-48px">
		<div
			class="col-span-12 !mx-[-20px] !my-[-5px] flex items-center justify-center mdp:!mx-[-32px] mdl:!mx-0 lg:!m-[-10px]"
		>
			<div
				ref="swiperEl"
				class="swiper !px-[20px] !py-[5px] mdp:!px-[32px] mdl:!px-0 lg:!p-[10px]"
			>
				<div class="swiper-wrapper px-8px">
					<template
						v-for="(blog, index) in isLoading ? placeholders : data"
						:key="index"
					>
						<a
							:href="blog?.link"
							class="swiper-slide !h-[180px] !w-[320px] cursor-pointer overflow-hidden rounded-[8px] mdl:!h-[172px] mdl:!w-[306px] lg:!h-[216px] lg:!w-[384px]"
						>
							<div
								v-if="!blog || !isImageLoaded(blog.img_medium)"
								class="aspect-video animate-pulse bg-n-100"
							></div>

							<img
								v-if="blog"
								:src="blog.img_medium"
								loading="lazy"
								class="h-full w-full transition-transform duration-300 can-hover:hover:!scale-[1.05]"
								@load="handleImageLoad(blog.img_medium)"
								@click="handleBannerClick(index)"
							/>
						</a>
					</template>
				</div>

				<div
					v-show="!isTabletPortrait"
					class="swiper-pagination !relative mt-[37px] [&>.swiper-pagination-bullet-active]:bg-black [&>span]:bg-b-25% [&>span]:opacity-100"
				></div>
			</div>
		</div>
	</div>
</template>
